import { NotificationBadgeModel } from 'api-hooks/notification/model';
import { SalePurchaseTabEnum } from 'api-hooks/sale/common';
import { ReleaseStatusEnum } from 'common/constants/enum';
import Separator from 'components/common/separator';
import Tabs from 'components/common/tabs';
import { NotificationBadge } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

interface Props {
  setActiveKey: (value: React.SetStateAction<React.Key>) => void;
  activeKey: React.Key;
  setStatus: (value: React.SetStateAction<string | undefined>) => void;
  badge?: { [key: string]: NotificationBadgeModel };
}

export function useReleaseTabStatus() {
  const processedStatus = [ReleaseStatusEnum.Requested].join('|');

  const needApprovalStatus = [
    ReleaseStatusEnum.PendingApproval,
    ReleaseStatusEnum.PendingCancelRefund,
    ReleaseStatusEnum.PendingRefund,
    ReleaseStatusEnum.PendingReleaseApproval,
    ReleaseStatusEnum.Draft,
    ReleaseStatusEnum.DraftCancelRefund,
    ReleaseStatusEnum.DraftRefund,
  ].join('|');

  const rejectedApprovalStatus = [
    ReleaseStatusEnum.RejectRefund,
    ReleaseStatusEnum.RejectedApproval,
    ReleaseStatusEnum.RejectedReleaseApproval,
  ].join('|');

  const completeStatus = [
    ReleaseStatusEnum.Completed,
    ReleaseStatusEnum.CancelRefund,
  ].join('|');

  const allStatus = [
    processedStatus,
    needApprovalStatus,
    rejectedApprovalStatus,
    completeStatus,
  ].join('|');

  const tabStatus = {
    '0': allStatus,
    '1': processedStatus,
    '2': needApprovalStatus,
    '3': rejectedApprovalStatus,
    '4': completeStatus,
  } as const;

  return {
    processedStatus,
    needApprovalStatus,
    rejectedApprovalStatus,
    completeStatus,
    allStatus,
    tabStatus,
  };
}

export default function ReleaseTabList(props: Props) {
  const { t } = useTranslation();
  const { activeKey, setActiveKey, setStatus, badge } = props;
  const { tabStatus } = useReleaseTabStatus();
  return (
    <Tabs
      activeKey={activeKey}
      onChangeActive={({ activeKey }) => {
        setActiveKey(activeKey);
        setStatus(tabStatus[activeKey]);
      }}
      items={[
        { title: t('common:all'), component: <></> },
        {
          title: (
            <>
              {t('common:processed')}
              {!!badge?.[SalePurchaseTabEnum.Processed] &&
                !!badge?.[SalePurchaseTabEnum.Processed].count && (
                  <>
                    <Separator gap={8} />
                    <NotificationBadge
                      style={{
                        backgroundColor:
                          badge?.[SalePurchaseTabEnum.Processed]
                            .background_color,
                        color: badge?.[SalePurchaseTabEnum.Processed].color,
                      }}
                    >
                      {badge?.[SalePurchaseTabEnum.Processed].count}
                    </NotificationBadge>
                  </>
                )}
            </>
          ),
          component: <></>,
        },
        {
          title: (
            <>
              {t('common:need_approval')}
              {!!badge?.[SalePurchaseTabEnum.NeedApproval] &&
                !!badge?.[SalePurchaseTabEnum.NeedApproval].count && (
                  <>
                    <Separator gap={8} />
                    <NotificationBadge
                      style={{
                        backgroundColor:
                          badge?.[SalePurchaseTabEnum.NeedApproval]
                            .background_color,
                        color: badge?.[SalePurchaseTabEnum.NeedApproval].color,
                      }}
                    >
                      {badge?.[SalePurchaseTabEnum.NeedApproval].count}
                    </NotificationBadge>
                  </>
                )}
            </>
          ),
          component: <></>,
        },
        {
          title: (
            <>
              {t('common:rejected_approval')}
              {!!badge?.[SalePurchaseTabEnum.RejectedApproval] &&
                !!badge?.[SalePurchaseTabEnum.RejectedApproval].count && (
                  <>
                    <Separator gap={8} />
                    <NotificationBadge
                      style={{
                        backgroundColor:
                          badge?.[SalePurchaseTabEnum.RejectedApproval]
                            .background_color,
                        color:
                          badge?.[SalePurchaseTabEnum.RejectedApproval].color,
                      }}
                    >
                      {badge?.[SalePurchaseTabEnum.RejectedApproval].count}
                    </NotificationBadge>
                  </>
                )}
            </>
          ),
          component: <></>,
        },
        { title: t('common:complete'), component: <></> },
      ]}
      overrideStyles={{
        tabList: { minHeight: '0px' },
        tab: { minHeight: '0px', height: '0', flexGrow: '0' },
        tabPanel: { minHeight: '0px', height: '0', flexGrow: '0' },
      }}
    />
  );
}
